'use client';

import { PrevNext } from '@/components/CommonComponents/PrevNext';
import { Stepper } from '@/components/CommonComponents/Stepper';
import PageHeaderWrapper from '@/components/Layout/PageHeaderWrapper';
import { showToast } from '@/helper/toast.helper';
import { AppDispatch, RootState } from '@/store';
import {
  createNotification,
  updateNotification,
} from '@/store/slices/notificationManagementSlice/notificationManagementSlice';
import { getProjectsDropdown } from '@/store/slices/projectsSlice/projectsSlice';
import { getSitesByProjectId } from '@/store/slices/siteManagementSlice/siteManagementSlice';
import {
  getSurveySetBySiteId,
  resetPosterCodeState,
} from '@/store/slices/surveySetManagementSlice/surveySetManagementSlice';
import { CreateNotifications } from '@/types/notification.type';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import MessageContent from './MessageContent';
import PreviewAndSend from './PreviewAndSend';
import ScheduleAndSettings from './ScheduleAndSettings';
import TargetAudience from './TargetAudience';

type FromSource = 'dashboard' | 'notification-management';

export default function CreateNotification() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [step, setStep] = useState(0);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [notificationId, setNotificationId] = useState('');
  const [recipientCount, setRecipientCount] = useState(0);
  const isCreating = useSelector((state: RootState) => state.notification.isCreating);
  const isSavingDraft = useSelector((state: RootState) => state.notification.isSavingDraft);
  const queryParams = new URLSearchParams(location.search);
  const isEditFlow = queryParams.get('mode') === 'edit';
  const scheduleRef = useRef<any>(null);

  const targetAudienceRef = useRef<any>(null);

  const projects = useSelector((state: RootState) => state.projects.list.projectsDropdown);
  const { sitesByProject } = useSelector((state: RootState) => state.sites);
  const estimatedCount = useSelector((state: RootState) => state.surveySet.estimatedCount);

  const surveySetList = useSelector(
    (state: RootState) => state.surveySet.surveysetsBySite.data || [],
  );

  const fromProject = location.state?.fromProject as { id: number; name: string } | undefined;

  const [messageData, setMessageData] = useState<CreateNotifications>({
    title: '',
    message: '',
    projectId: fromProject?.id ?? null,
    siteId: null,
    fileUrl: '',
    notificationType: 'PUSH_NOTIFICATION',
    deliveryType: 'IMMEDIATE',
    targetGroup: fromProject ? 'FILTER_USERS' : 'ALL_USERS',
    scheduledTimer: '',
    isTested: null,
    status: null,
    fileData: null,
  });

  useEffect(() => {
    dispatch(getProjectsDropdown());
    if (fromProject?.id) {
      dispatch(getSitesByProjectId(fromProject.id));
    }
  }, [dispatch]);

  useEffect(() => {
    const editData = location.state?.notification;

    if (editData && editData.status === 'SENT') {
      showToast.error("Notification is sent. Can't edit right now");
    }

    if (isEditFlow && editData) {
      const derived = { ...editData } as any;
      if (!derived.projectId && derived.receivers && derived.receivers.length > 0) {
        derived.projectId = derived.receivers[0].projectId ?? derived.projectId;
      }
      if (!derived.siteId && derived.receivers && derived.receivers.length > 0) {
        derived.siteId = derived.receivers[0].siteId ?? derived.siteId;
      }
      if (!derived.surveySetId && derived.receivers && derived.receivers.length > 0) {
        derived.surveySetId = derived.receivers[0].surveySetId ?? derived.surveySetId;
      }

      setMessageData(derived);

      if (derived.projectId) {
        dispatch(getSitesByProjectId(derived.projectId));
      }
      if (derived.siteId) {
        dispatch(getSurveySetBySiteId(derived.siteId));
      }
    }
  }, []);

  useEffect(() => {
    dispatch(resetPosterCodeState());
  }, []);

  useEffect(() => {
    if (!isEditFlow || !location.state?.notification) return;

    const editData = location.state.notification;

    if ((projects ?? []).length > 0 && editData.projectId && !messageData.projectId) {
      setMessageData((prev) => ({ ...prev, projectId: editData.projectId }));
    }

    if ((sitesByProject ?? []).length > 0 && editData.siteId && !messageData.siteId) {
      setMessageData((prev) => ({ ...prev, siteId: editData.siteId }));
    }

    if ((surveySetList ?? []).length > 0 && editData.surveySetId && !messageData.surveySetId) {
      setMessageData((prev) => ({ ...prev, surveySetId: editData.surveySetId }));
    }

    if ((surveySetList ?? []).length > 0) {
      const surveyFromList = surveySetList.find(
        (s: any) => s.id === (editData.surveySetId || messageData.surveySetId),
      );
      if (surveyFromList && surveyFromList.posterCode && !messageData.posterCode) {
        setMessageData((prev) => ({ ...prev, posterCode: surveyFromList.posterCode }));
      }
    }

    if (editData.posterCode) {
      setMessageData((prev) => ({ ...prev, posterCode: editData.posterCode }));
    }
  }, [projects, sitesByProject, surveySetList]);

  useEffect(() => {
    if (messageData.projectId) {
      dispatch(getSitesByProjectId(messageData.projectId));
    }
  }, [dispatch, messageData.projectId]);

  const handleReset = (_flag?: boolean) => {
    if (step === 1) {
      setMessageData((prev) => ({
        ...prev,
        targetGroup: 'ALL_USERS',
        projectId: null,
        siteId: null,
        surveySetId: undefined,
        posterCode: '',
        fileUrl: '',
        fileData: null,
      }));
      return;
    }

    if (step === 2) {
      setMessageData((prev) => ({
        ...prev,
        deliveryType: 'IMMEDIATE',
        scheduledTimer: '',
        isTested: null,
      }));
      setIsConfirmed(false);
      return;
    }
    setMessageData({
      title: '',
      message: '',
      projectId: null,
      siteId: null,
      posterCode: '',
      fileUrl: '',
      notificationType: 'PUSH_NOTIFICATION',
      deliveryType: 'IMMEDIATE',
      targetGroup: 'ALL_USERS',
      scheduledTimer: '',
      isTested: null,
      status: null,
      fileData: null,
    });

    setIsConfirmed(false);
  };

  const handleSaveDraft = async () => {
    try {
      const formData = new FormData();

      formData.append('title', messageData.title);
      formData.append('message', messageData.message);
      formData.append('notificationType', messageData.notificationType);
      formData.append('targetGroup', messageData.targetGroup);
      formData.append('deliveryType', messageData.deliveryType);
      formData.append('status', 'DRAFT');

      if (messageData.scheduledTimer) {
        formData.append('scheduledTimer', messageData.scheduledTimer);
      }
      if (messageData.projectId) {
        formData.append('projectId', messageData.projectId.toString());
      }
      if (messageData.siteId) {
        formData.append('siteId', messageData.siteId.toString());
      }
      if (messageData.surveySetId) {
        formData.append('surveySetId', messageData.surveySetId.toString());
      }
      if (messageData.posterCode) {
        formData.append('posterCode', messageData.posterCode);
      }

      if (messageData.targetGroup === 'BULK_USERS' && messageData.fileData) {
        const buffer = Uint8Array.from(atob(messageData.fileData.content), (c) => c.charCodeAt(0));
        const file = new File([buffer], messageData.fileData.name, {
          type: messageData.fileData.type,
        });
        formData.append('file', file);
      }

      if (isEditFlow) {
        const id = location.state.notification.id;
        await dispatch(updateNotification({ id, formData })).unwrap();
      } else {
        await dispatch(createNotification(formData)).unwrap();
      }
      showToast.success('Draft saved successfully');
    } catch {
      showToast.error('Failed to save draft');
    }
  };
  const isNextDisabled = useMemo(() => {
    if (step === 0) {
      const isTitleValid =
        messageData.title.trim().length >= 5 && messageData.title.trim().length <= 30;
      const isMessageValid =
        messageData.message.trim().length >= 10 && messageData.message.trim().length <= 500;
      return !(isTitleValid && isMessageValid);
    }
    return false;
  }, [step, messageData.title, messageData.message]);

  const handleSend = () => {
    // console.log('Sending notification...');
  };

  const handleCreate = async () => {
    try {
      const formData = new FormData();

      formData.append('title', messageData.title);
      formData.append('message', messageData.message);
      formData.append('notificationType', messageData.notificationType);
      formData.append('targetGroup', messageData.targetGroup);
      formData.append('deliveryType', messageData.deliveryType);

      if (messageData.isTested === true) {
        formData.append('isTested', 'true');
      }

      if (messageData.scheduledTimer) {
        formData.append('scheduledTimer', messageData.scheduledTimer);
      }
      if (messageData.projectId) {
        formData.append('projectId', messageData.projectId.toString());
      }
      if (messageData.siteId) {
        formData.append('siteId', messageData.siteId.toString());
      }
      if (messageData.surveySetId) {
        formData.append('surveySetId', messageData.surveySetId.toString());
      }
      if (messageData.posterCode) {
        formData.append('posterCode', messageData.posterCode);
      }

      if (messageData.targetGroup === 'BULK_USERS' && messageData.fileData) {
        const buffer = Uint8Array.from(atob(messageData.fileData.content), (c) => c.charCodeAt(0));
        const file = new File([buffer], messageData.fileData.name, {
          type: messageData.fileData.type,
        });
        formData.append('file', file);
      }

      if (isEditFlow) {
        const id = location.state.notification.id;
        const result = await dispatch(updateNotification({ id, formData })).unwrap();
        setNotificationId(id);
        setRecipientCount(result?.recipientCount || 0);
        setShowSuccessPopup(true);
      } else {
        const result = await dispatch(createNotification(formData)).unwrap();
        setNotificationId(result.data.notification.id);
        setRecipientCount(result.data.recipientCount || 0);
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error('Error creating/updating notification:', error);
      showToast.error('Something went wrong');
    }
  };

  const goBack = () => {
    // console.log('Back');
  };

  const handlePrev = () => {
    setStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleNext = () => {
    if (step === 0) {
      if (!messageData.title?.trim() || !messageData.message?.trim()) {
        showToast.error('Please fill Title and Message');
        return;
      }
    }
    if (step === 1) {
      // Check if message contains template variables but target is not bulk upload
      const templateVariables = extractTemplateVariables(messageData.message);
      if (templateVariables.length > 0 && messageData.targetGroup !== 'BULK_USERS') {
        showToast.error('Dynamic variables can only be used with Bulk Upload target audience');
        return;
      }
      if (targetAudienceRef.current) {
        const isValid = targetAudienceRef.current.validate();
        if (!isValid) {
          showToast.error('Please fill all required fields in Target Audience');
          return;
        }
      }
    }
    if (step === 2) {
      if (scheduleRef.current) {
        const isValid = scheduleRef.current.validate();
        if (!isValid) {
          showToast.error('Please select valid date & time');
          return;
        }
      }
    }
    setStep((prevStep) => Math.min(prevStep + 1, 3));
  };

  const extractTemplateVariables = (text: string): string[] => {
    const regex = /\{\{\s*(\w+)\s*\}\}/g;
    const variables: string[] = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }
    return variables;
  };

  // Check if message has dynamic variables
  const hasDynamicVariables = useMemo(() => {
    return extractTemplateVariables(messageData.message).length > 0;
  }, [messageData.message]);

  // Auto-select BULK_USERS when dynamic variables are detected
  useEffect(() => {
    if (hasDynamicVariables && messageData.targetGroup !== 'BULK_USERS') {
      setMessageData((prev) => ({ ...prev, targetGroup: 'BULK_USERS' }));
    }
  }, [hasDynamicVariables, messageData.targetGroup]);

  const projName = projects?.find((p: any) => p.id === messageData.projectId)?.name || '';
  const siteName = sitesByProject?.find((s: any) => s.id === messageData.siteId)?.name || '';
  const surveySetName =
    surveySetList?.find((s: any) => s.id === messageData.surveySetId)?.title || 'NA';
  const scheduled = messageData.scheduledTimer ? new Date(messageData.scheduledTimer) : null;
  const sendTime = scheduled
    ? scheduled.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    : '';
  const sendDate = scheduled ? scheduled.toLocaleDateString('en-GB').replace(/\//g, '-') : '';

  const humanType = messageData.notificationType
    .split('_')
    .slice(0, -1)
    .join(' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

  const humanAudience = messageData.targetGroup
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

  return (
    <PageHeaderWrapper
      title={isEditFlow ? 'Notification Management' : 'Notification Management'}
      description={
        isEditFlow
          ? 'Manage and monitor all notifications across the platform'
          : 'Manage and monitor all notifications across the platform'
      }
      onAdd={() => navigate(-1)}
      addLabel={isEditFlow ? 'Return to Notification List' : 'Return to Notification List'}
    >
      {/* Step Indicator with subtitles */}
      <Stepper
        steps={[
          {
            title: isEditFlow ? 'Message Content' : 'Message Content',
            subtitle: 'Title & Body',
          },
          { title: 'Target Audience', subtitle: 'Who to send to' },
          { title: 'Schedule & Settings', subtitle: 'When to send' },
          {
            title: isEditFlow ? 'Preview & Save' : 'Preview & Save',
            subtitle: 'Final check & confirmation ',
          },
        ]}
        activeStep={step}
      />

      {/* Sections */}
      <div className="bg-white shadow-md rounded-xl ">
        {step === 0 && (
          <MessageContent messageData={messageData!} setMessageData={setMessageData} />
        )}
        {step === 1 && (
          <TargetAudience
            ref={targetAudienceRef}
            messageData={messageData!}
            setMessageData={setMessageData}
            hasDynamicVariables={hasDynamicVariables}
            lockedProjectId={fromProject?.id}
            lockedProjectName={fromProject?.name}
          />
        )}
        {step === 2 && (
          <ScheduleAndSettings
            ref={scheduleRef}
            messageData={messageData}
            setMessageData={setMessageData}
          />
        )}
        {step === 3 && (
          <PreviewAndSend
            details={{
              notificationType: humanType,
              title: messageData.title,
              message: messageData.message,
              targetAudience: humanAudience,
              project: projName || 'NA',
              site: siteName || 'NA',
              survey: surveySetName || 'NA',
              sendTime,
              sendDate,
              estimatedRecipients: estimatedCount ?? null,
            }}
            isConfirmed={isConfirmed}
            setIsConfirmed={setIsConfirmed}
            onSend={handleSend}
            onSaveDraft={handleSaveDraft}
            onPrevious={goBack}
            isEdit={true}
          />
        )}

        <div className="px-7 pb-6">
          <PrevNext
            hidePrevious={step === 0}
            prev={handlePrev}
            onReset={
              step === 3 || (step === 1 && messageData.targetGroup === 'ALL_USERS')
                ? undefined
                : handleReset
            }
            disableNext={isNextDisabled}
            next={step < 3 ? handleNext : undefined}
            create={step === 3}
            createLabel={isEditFlow ? 'Update Notification →' : 'Create Notification →'}
            onCreate={step === 3 ? handleCreate : undefined}
            disableCreate={step === 3 && !isConfirmed}
            onSaveDraft={step === 3 ? handleSaveDraft : undefined}
            isSubmitting={isCreating}
            isSavingDraft={isSavingDraft}
            // isEditMode={isEditFlow}
          />
        </div>
      </div>

      {showSuccessPopup && (
        <div
          className="fixed right-0 bottom-0 left-[255px] top-[72px] z-50 flex items-center justify-center"
          style={{
            background:
              'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
          }}
        >
          <div className="bg-[#621C43] text-white w-[480px] rounded-xl shadow-xl relative">
            <div className="px-6  border-b border-[#a09f9c]">
              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  navigate('/dashboard/notification-management');
                }}
                className="absolute right-4 top-4 text-[#FFF8ED] hover:text-gray-200"
              >
                ✕
              </button>

              <div className="flex items-center ml-20 text-lg font-bold p-3 gap-2">
                {/* <span className="text-[#FFF8ED] text-lg font-medium">✓</span> */}
                {isEditFlow
                  ? 'Notification Updated Successfully'
                  : 'Notification Created Successfully'}
              </div>
            </div>

            <p className="text-center font-medium text-md mb-4 pt-3">
              <strong>Notification ID:</strong> {notificationId} <br />
              <strong>Recipients:</strong> {recipientCount} users
            </p>

            <div className="px-6">
              <p className="font-medium text-[#FFF8ED]">Status:</p>
              <div className="bg-[#FFF8ED]/20 px-2 py-1 rounded-xl text-sm mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-[#FFF8ED] text-lg font-medium">✓</span>
                  Notification Created Successfully
                </div>
              </div>
            </div>

            <div className="px-6 pb-4">
              {!isEditFlow ? (
                <div className="flex justify-between">
                  <button
                    className="bg-[#0BA96F] hover:bg-[#0BA96F]/80 font-medium px-4 py-2 rounded-lg text-[#FFF8ED]"
                    onClick={() => {
                      setShowSuccessPopup(false);
                      window.location.reload();
                    }}
                  >
                    Create Another Notification
                  </button>

                  <button
                    className="bg-[#4C86E7] hover:bg-[#4C86E7]/30 font-medium px-4 py-2 rounded-lg text-[#FFF8ED]"
                    onClick={() => {
                      setShowSuccessPopup(false);
                      navigate('/dashboard/notification-management');
                    }}
                  >
                    View Notification List
                  </button>
                </div>
              ) : (
                <button
                  className="bg-[#4C86E7] hover:bg-[#4C86E7]/30 font-medium w-full py-2 rounded-lg text-[#FFF8ED]"
                  onClick={() => {
                    setShowSuccessPopup(false);
                    navigate('/dashboard/notification-management');
                  }}
                >
                  View Notification List
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </PageHeaderWrapper>
  );
}
